import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLoadScript, GoogleMap, Polygon, Marker, InfoWindow, Polyline } from "@react-google-maps/api";

const API_KEY = "AIzaSyAqQuX2O1PHI9uihjJnOrYHwkkyxdNcfNo";

interface Office {
    id: number;
    name: string;
    lat: string;
    long: string;
    status: boolean;
    coordinates: number[][][];
}

interface ProcessedOffice {
    id: number;
    name: string;
    lat: number;
    lng: number;
    status: boolean;
    polygons: Array<Array<{ lat: number; lng: number }>>;
}


const OfficeLocationMap: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [offices, setOffices] = useState<ProcessedOffice[]>([]);
    const [selectedOffice, setSelectedOffice] = useState<ProcessedOffice | null>(null);
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 28.6139, lng: 77.2090 }); // Default to Delhi
    const [error, setError] = useState<string | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: API_KEY,
        libraries: ["places", "drawing", "geometry"] as any,
    });

    // Decode data from URL parameters: lat, long, coordinates
    useEffect(() => {
        const latParam = searchParams.get('lat');
        const longParam = searchParams.get('long');
        const coordinatesParam = searchParams.get('coordinates');

        // Check if we have the new format (lat, long, coordinates)
        if (latParam && longParam && coordinatesParam) {
            try {
                const lat = parseFloat(latParam);
                const lng = parseFloat(longParam);

                // Log base64 data
                console.log('=== Base64 Data (New Format) ===');
                console.log('Raw base64 string:', coordinatesParam);
                console.log('Base64 string length:', coordinatesParam.length);

                // Decode base64 coordinates
                const decodedString = atob(coordinatesParam);
                console.log('Decoded string:', decodedString);
                console.log('Decoded string length:', decodedString.length);

                const coordinatesData = JSON.parse(decodedString);
                console.log('Parsed coordinates data:', coordinatesData);
                console.log('Coordinates data type:', Array.isArray(coordinatesData) ? 'Array' : typeof coordinatesData);
                console.log('================================');

                // Process coordinates - handle both formats:
                // 1. Direct coordinates array: [[[lng, lat], ...], ...]
                // 2. Office objects array: [{coordinates: [[[lng, lat], ...]], ...}, ...]
                const polygons: Array<Array<{ lat: number; lng: number }>> = [];
                let officeName = 'Office Location';
                let officeId = 1;
                let officeStatus = true;

                if (Array.isArray(coordinatesData)) {
                    console.log('=== Processing Coordinates Data ===');
                    console.log('Coordinates data is array, length:', coordinatesData.length);

                    // Check if first element is an office object or coordinates array
                    const firstItem = coordinatesData[0];
                    console.log('First item:', firstItem);
                    console.log('First item type:', typeof firstItem);
                    console.log('First item is array:', Array.isArray(firstItem));
                    console.log('First item has coordinates property:', firstItem && typeof firstItem === 'object' && firstItem.coordinates);

                    if (firstItem && typeof firstItem === 'object' && firstItem.coordinates) {
                        console.log('Processing as office objects array');
                        // It's an array of office objects
                        coordinatesData.forEach((office: any, index: number) => {
                            if (office.coordinates && Array.isArray(office.coordinates)) {
                                // Use office name and id from first office
                                if (index === 0) {
                                    officeName = office.name || 'Office Location';
                                    officeId = office.id || 1;
                                    officeStatus = office.status !== undefined ? office.status : true;
                                }

                                // Process each polygon in this office's coordinates
                                office.coordinates.forEach((polygonCoords: number[][]) => {
                                    if (Array.isArray(polygonCoords) && polygonCoords.length > 0) {
                                        // Convert from [lng, lat] format to {lat, lng} format
                                        const convertedPolygon = polygonCoords
                                            .map((coord: number[]) => {
                                                if (Array.isArray(coord) && coord.length >= 2) {
                                                    const lat = parseFloat(coord[1].toString());
                                                    const lng = parseFloat(coord[0].toString());
                                                    // Validate coordinates are valid numbers and within valid ranges
                                                    if (!isNaN(lat) && !isNaN(lng) &&
                                                        lat >= -90 && lat <= 90 &&
                                                        lng >= -180 && lng <= 180) {
                                                        return { lat, lng };
                                                    }
                                                }
                                                return null;
                                            })
                                            .filter((coord): coord is { lat: number; lng: number } => coord !== null);

                                        if (convertedPolygon.length >= 3) {
                                            polygons.push(convertedPolygon);
                                            console.log(`Added polygon with ${convertedPolygon.length} points`);
                                        }
                                    }
                                });
                            }
                        });
                    } else if (Array.isArray(firstItem)) {
                        console.log('Processing as direct coordinates array');
                        // It's a direct coordinates array: [[[lng, lat], ...], ...]
                        coordinatesData.forEach((polygonCoords: number[][], polygonIndex: number) => {
                            console.log(`Processing polygon ${polygonIndex}:`, polygonCoords);
                            if (Array.isArray(polygonCoords) && polygonCoords.length > 0) {
                                console.log(`Polygon ${polygonIndex} has ${polygonCoords.length} points`);
                                // Convert from [lng, lat] format to {lat, lng} format
                                const convertedPolygon = polygonCoords
                                    .map((coord: number[], coordIndex: number) => {
                                        if (Array.isArray(coord) && coord.length >= 2) {
                                            const lat = parseFloat(coord[1].toString());
                                            const lng = parseFloat(coord[0].toString());
                                            // Validate coordinates are valid numbers and within valid ranges
                                            if (!isNaN(lat) && !isNaN(lng) &&
                                                lat >= -90 && lat <= 90 &&
                                                lng >= -180 && lng <= 180) {
                                                if (coordIndex === 0) {
                                                    console.log(`First coordinate converted:`, { lat, lng, original: coord });
                                                }
                                                return { lat, lng };
                                            } else {
                                                console.warn(`Invalid coordinate at index ${coordIndex}:`, coord);
                                            }
                                        }
                                        return null;
                                    })
                                    .filter((coord): coord is { lat: number; lng: number } => coord !== null);

                                console.log(`Polygon ${polygonIndex} converted: ${convertedPolygon.length} valid points`);
                                if (convertedPolygon.length >= 3) {
                                    polygons.push(convertedPolygon);
                                    console.log(`✓ Added polygon ${polygonIndex} with ${convertedPolygon.length} points`);
                                } else {
                                    console.warn(`✗ Polygon ${polygonIndex} has only ${convertedPolygon.length} points (need at least 3)`);
                                }
                            } else {
                                console.warn(`Polygon ${polygonIndex} is not a valid array or is empty`);
                            }
                        });
                    } else {
                        console.warn('Unknown coordinates data format:', firstItem);
                    }
                    console.log(`Total polygons created: ${polygons.length}`);
                    console.log('================================');
                }

                // Always create office object with marker at provided lat/lng
                // The marker location is from URL params, polygons are from base64
                console.log('=== Creating Office Object ===');
                console.log('Polygons array before creating office:', polygons);
                console.log('Polygons count:', polygons.length);

                const office: ProcessedOffice = {
                    id: officeId,
                    name: officeName,
                    lat: lat,  // Marker location from URL params
                    lng: lng,  // Marker location from URL params
                    status: officeStatus,
                    polygons: polygons  // Polygons from base64 coordinates
                };

                console.log('Created office object:', office);
                console.log('Office polygons:', office.polygons);
                console.log('Office polygons length:', office.polygons.length);
                console.log('Pin point marker location (from URL):', { lat, lng });

                if (polygons.length > 0) {
                    console.log('First polygon points:', polygons[0]?.length);
                    console.log('First polygon coordinates:', JSON.stringify(polygons[0]));
                    console.log('First polygon first point:', polygons[0]?.[0]);
                    console.log('First polygon last point:', polygons[0]?.[polygons[0].length - 1]);
                } else {
                    console.warn('⚠️ NO POLYGONS CREATED!');
                }
                console.log('================================');

                // Calculate map center (use first polygon center if available, otherwise marker location)
                // This matches the logic from attendanceDetailsModel
                const mapCenter = polygons.length > 0 && polygons[0] && polygons[0].length > 0
                    ? (() => {
                        const firstPolygon = polygons[0];
                        const center = firstPolygon.reduce((acc, point) => ({
                            lat: acc.lat + point.lat,
                            lng: acc.lng + point.lng
                        }), { lat: 0, lng: 0 });
                        return {
                            lat: center.lat / firstPolygon.length,
                            lng: center.lng / firstPolygon.length
                        };
                    })()
                    : { lat, lng };

                console.log('Map center calculated:', mapCenter);
                console.log('Marker location:', { lat, lng });
                if (polygons.length > 0) {
                    console.log('Using polygon center for map view');
                } else {
                    console.log('Using marker location for map view');
                }

                setOffices([office]);
                setMapCenter(mapCenter);

            } catch (err: any) {
                console.error('Error decoding coordinates:', err);
                setError(`Error decoding coordinates: ${err.message}`);
            }
            return;
        }

        // Check if we have only lat and long (pin point only, no polygons)
        if (latParam && longParam && !coordinatesParam) {
            try {
                const lat = parseFloat(latParam);
                const lng = parseFloat(longParam);

                // Validate coordinates
                if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                    setError('Invalid latitude or longitude values');
                    return;
                }

                console.log('=== Creating Pin Point Only ===');
                console.log('Latitude:', lat);
                console.log('Longitude:', lng);

                // Create office object with just marker location (no polygons)
                const office: ProcessedOffice = {
                    id: 1,
                    name: 'Office Location',
                    lat: lat,
                    lng: lng,
                    status: true,
                    polygons: [] // No polygons, just a marker
                };

                console.log('Created office marker:', office);
                setOffices([office]);
                setMapCenter({ lat, lng });
                return;
            } catch (err: any) {
                console.error('Error creating pin point:', err);
                setError(`Error creating pin point: ${err.message}`);
                return;
            }
        }

        // Fallback to old format (data parameter)
        const base64Data = searchParams.get('data');
        if (!base64Data) {
            // If no data and no lat/long, show error
            if (!latParam || !longParam) {
                setError('No data found in URL. Expected: /map?lat=...&long=... or /map?lat=...&long=...&coordinates=...');
            }
            return;
        }

        try {
            // Log base64 data
            console.log('=== Base64 Data (Old Format) ===');
            console.log('Raw base64 string:', base64Data);
            console.log('Base64 string length:', base64Data.length);

            // Decode base64
            const decodedString = atob(base64Data);
            console.log('Decoded string:', decodedString);
            console.log('Decoded string length:', decodedString.length);

            const jsonData = JSON.parse(decodedString);
            console.log('Parsed JSON data:', jsonData);
            console.log('JSON data type:', Array.isArray(jsonData) ? 'Array' : typeof jsonData);
            console.log('================================');

            // Handle both array directly or wrapped in object
            let officesArray: Office[] = [];
            if (Array.isArray(jsonData)) {
                // Direct array
                officesArray = jsonData;
            } else if (jsonData.offices && Array.isArray(jsonData.offices)) {
                // Wrapped in object
                officesArray = jsonData.offices;
            } else {
                setError('Invalid data format: Expected array of offices');
                return;
            }

            // Process offices data
            if (officesArray.length > 0) {
                const processedOffices: ProcessedOffice[] = officesArray.map((office: Office) => {
                    const polygons: Array<Array<{ lat: number; lng: number }>> = [];

                    // Process each polygon in coordinates
                    if (office.coordinates && Array.isArray(office.coordinates)) {
                        office.coordinates.forEach((polygonCoords: number[][]) => {
                            if (Array.isArray(polygonCoords) && polygonCoords.length > 0) {
                                // Convert from [lng, lat] format to {lat, lng} format
                                const convertedPolygon = polygonCoords
                                    .map((coord: number[]) => {
                                        if (Array.isArray(coord) && coord.length >= 2) {
                                            const lat = parseFloat(coord[1].toString());
                                            const lng = parseFloat(coord[0].toString());
                                            // Validate coordinates are valid numbers and within valid ranges
                                            if (!isNaN(lat) && !isNaN(lng) &&
                                                lat >= -90 && lat <= 90 &&
                                                lng >= -180 && lng <= 180) {
                                                return { lat, lng };
                                            }
                                        }
                                        return null;
                                    })
                                    .filter((coord): coord is { lat: number; lng: number } => coord !== null);

                                if (convertedPolygon.length >= 3) {
                                    polygons.push(convertedPolygon);
                                }
                            }
                        });
                    }

                    return {
                        id: office.id,
                        name: office.name,
                        lat: parseFloat(office.lat),
                        lng: parseFloat(office.long),
                        status: office.status,
                        polygons: polygons
                    };
                });

                setOffices(processedOffices);

                // Set map center to first office location
                if (processedOffices.length > 0) {
                    setMapCenter({
                        lat: processedOffices[0].lat,
                        lng: processedOffices[0].lng
                    });
                }
            } else {
                setError('No offices found in data');
            }
        } catch (err: any) {
            console.error('Error decoding base64 data:', err);
            setError(`Error decoding data: ${err.message}`);
        }
    }, [searchParams]);

    // Fit map bounds to show all polygons and markers (similar to attendanceDetailsModel approach)
    useEffect(() => {
        if (mapRef.current && offices.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            let hasValidPolygons = false;
            let hasMarkers = false;

            offices.forEach((office) => {
                // Always add office marker location to bounds
                if (office.lat && office.lng) {
                    bounds.extend(new google.maps.LatLng(office.lat, office.lng));
                    hasMarkers = true;
                }

                // Add all polygon points to bounds
                office.polygons.forEach((polygon) => {
                    if (polygon && polygon.length >= 3) {
                        polygon.forEach((point) => {
                            bounds.extend(new google.maps.LatLng(point.lat, point.lng));
                            hasValidPolygons = true;
                        });
                    }
                });
            });

            if (hasValidPolygons || hasMarkers) {
                // Use setTimeout to ensure map is fully loaded before fitting bounds
                setTimeout(() => {
                    if (mapRef.current) {
                        const padding = 100;
                        mapRef.current.fitBounds(bounds, padding);
                        console.log('Map bounds adjusted to fit all polygons and markers');
                    }
                }, 100);
            }
        }
    }, [offices]);

    // Polygon colors
    const polygonColors = [
        { fill: '#1976D2', stroke: '#1976D2' }, // Blue
        { fill: '#28a745', stroke: '#28a745' }, // Green
        { fill: '#ffc107', stroke: '#ffc107' }, // Yellow
        { fill: '#dc3545', stroke: '#dc3545' }, // Red
        { fill: '#6f42c1', stroke: '#6f42c1' }, // Purple
    ];

    if (loadError) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <h2>Error loading Google Maps</h2>
                <p>{loadError.message}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <h2>Error</h2>
                <p>{error}</p>
                <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                    Please check the URL format. Expected: /map?lat=...&long=...&coordinates=...
                </p>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <h2>Loading Google Maps...</h2>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '100vh', position: 'relative', backgroundColor: '#f5f5f5' }}>
            {/* Header */}


            {/* Map */}
            <div style={{ width: '100%', height: '100%', marginTop: '' }}>
                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    zoom={offices.length > 0 && offices[0].polygons.length > 0 && offices[0].polygons[0]?.length >= 3 ? 17 : 15}
                    center={mapCenter}
                    onLoad={(map) => {
                        mapRef.current = map;
                        console.log('Map loaded');
                    }}
                    options={{
                        mapTypeControl: true,
                        streetViewControl: true,
                        fullscreenControl: true,
                        zoomControl: true,
                        styles: [
                            {
                                featureType: "poi",
                                elementType: "labels",
                                stylers: [{ visibility: "off" }]
                            }
                        ]
                    }}
                >
                    {/* Render all polygons for all offices (similar to attendanceDetailsModel) */}
                    {offices.flatMap((office, officeIndex) =>
                        office.polygons.map((polygon, polygonIndex) => {
                            if (!polygon || polygon.length < 3) {
                                return null;
                            }
                            const colorIndex = (officeIndex + polygonIndex) % polygonColors.length;
                            const colors = polygonColors[colorIndex];

                            return (
                                <Polygon
                                    key={`office-${office.id}-polygon-${polygonIndex}`}
                                    paths={polygon}
                                    options={{
                                        fillColor: colors.fill,
                                        fillOpacity: 0.2,
                                        strokeColor: colors.stroke,
                                        strokeWeight: 3,
                                        clickable: true,
                                        draggable: false,
                                        editable: false,
                                        geodesic: false,
                                        zIndex: 1
                                    }}
                                    onClick={() => setSelectedOffice(office)}
                                />
                            );
                        })
                    )}

                    {/* Lines from office polygon center to pin location */}
                    {offices.map((office) => {
                        // Calculate center of the first polygon (office location)
                        let officeCenter: { lat: number; lng: number } | null = null;

                        if (office.polygons.length > 0 && office.polygons[0] && office.polygons[0].length >= 3) {
                            const firstPolygon = office.polygons[0];
                            const center = firstPolygon.reduce((acc, point) => ({
                                lat: acc.lat + point.lat,
                                lng: acc.lng + point.lng
                            }), { lat: 0, lng: 0 });
                            officeCenter = {
                                lat: center.lat / firstPolygon.length,
                                lng: center.lng / firstPolygon.length
                            };
                        }

                        // Only draw line if we have a valid office center and pin location
                        if (officeCenter && office.lat && office.lng) {
                            return (
                                <Polyline
                                    key={`office-line-${office.id}`}
                                    path={[
                                        { lat: officeCenter.lat, lng: officeCenter.lng },
                                        { lat: office.lat, lng: office.lng }
                                    ]}
                                    options={{
                                        strokeColor: '#FF0000',
                                        strokeOpacity: 0.8,
                                        strokeWeight: 2,
                                        geodesic: false,
                                        zIndex: 2
                                    }}
                                />
                            );
                        }
                        return null;
                    })}

                    {/* Office markers */}
                    {offices.map((office) => (
                        <Marker
                            key={`office-marker-${office.id}`}
                            position={{ lat: office.lat, lng: office.lng }}
                            onClick={() => setSelectedOffice(office)}
                            icon={{
                                url: office.status
                                    ? 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
                                    : 'http://maps.google.com/mapfiles/ms/icons/gray-dot.png',
                                scaledSize: window.google?.maps ? new window.google.maps.Size(40, 40) : undefined
                            }}
                        />
                    ))}

                    {/* Info Window */}
                    {selectedOffice && (
                        <InfoWindow
                            position={{ lat: selectedOffice.lat, lng: selectedOffice.lng }}
                            onCloseClick={() => setSelectedOffice(null)}
                        >
                            <div style={{ padding: '10px', maxWidth: '250px' }}>
                                <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold' }}>
                                    {selectedOffice.name}
                                </h3>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                    <strong>ID:</strong> {selectedOffice.id}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                    <strong>Status:</strong> {selectedOffice.status ? 'Active' : 'Inactive'}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                    <strong>Location:</strong> {selectedOffice.lat.toFixed(6)}, {selectedOffice.lng.toFixed(6)}
                                </p>
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>
                                    <strong>Polygons:</strong> {selectedOffice.polygons.length}
                                </p>
                            </div>
                        </InfoWindow>
                    )}
                </GoogleMap>
            </div>

            {/* Office List Sidebar */}

        </div>
    );
};

export default OfficeLocationMap;

